import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { cancelRsvp, rsvpToLiveQuiz } from '../services/liveQuizService';
import { QuizListResponse } from '../types/quiz';

/** Every cached list this quiz's RSVP state could appear in. */
const PUBLISHED_QUIZZES_KEY = ['quizzes', 'published'];
const UPCOMING_LIVE_KEY = ['live', 'upcoming'];

interface RsvpMutationContext {
  /** Every matching cached query, snapshotted before the optimistic patch. */
  previousLists: Array<[QueryKey, QuizListResponse | undefined]>;
}

/**
 * Optimistic RSVP toggle for a single live quiz.
 *
 * Patches the cached `['quizzes','published']` list(s) immediately so the
 * button doesn't feel laggy, and rolls back to the exact pre-mutation
 * snapshot on failure — restoring just the `rsvped` flag would be wrong if
 * `rsvpCount` had also changed from, say, a background refetch landing in
 * between. The caller is responsible for ignoring clicks while `isPending`:
 * a double-toggled count is worse than a slow button.
 *
 * Uses a partial key match (`exact: false`, react-query's default) on
 * `['quizzes','published']` so this keeps working if that query ever grows
 * page/filter segments.
 */
export function useQuizRsvp(quizId: number) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, boolean, RsvpMutationContext>({
    mutationFn: (nextRsvped: boolean) =>
      nextRsvped ? rsvpToLiveQuiz(quizId) : cancelRsvp(quizId),

    onMutate: async (nextRsvped: boolean) => {
      await queryClient.cancelQueries({ queryKey: PUBLISHED_QUIZZES_KEY });

      const previousLists =
        queryClient.getQueriesData<QuizListResponse>({ queryKey: PUBLISHED_QUIZZES_KEY });

      queryClient.setQueriesData<QuizListResponse>(
        { queryKey: PUBLISHED_QUIZZES_KEY },
        data => {
          if (!data) {
            return data;
          }
          return {
            ...data,
            quizzes: data.quizzes.map(quiz => {
              if (quiz.id !== quizId || !quiz.live) {
                return quiz;
              }
              return {
                ...quiz,
                live: {
                  ...quiz.live,
                  rsvped: nextRsvped,
                  rsvpCount: quiz.live.rsvpCount + (nextRsvped ? 1 : -1)
                }
              };
            })
          };
        }
      );

      return { previousLists };
    },

    onError: (_error, _nextRsvped, context) => {
      context?.previousLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PUBLISHED_QUIZZES_KEY });
      queryClient.invalidateQueries({ queryKey: UPCOMING_LIVE_KEY });
    }
  });
}
